import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { w } from '../../../../stays/wording';
import { saveGuide } from '../../../../api/stays-guide-client';
import { StaysApiError } from '../../../../api/stays-client';
import type { GuideBundle, GuideContentField, StaffGuide } from '../../../../interfaces/stays-guide';
import { AppButton } from '../../../button/app-button';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { Field, FieldError, FieldHint, Input, Label, Select } from '../../../form-kit';
import { Stepper } from '../../../stepper/stepper';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import toast from '../../../../utils/toast';
import {
  Banner,
  BannerText,
  ChecklistRow,
  FieldGrid,
  H2,
  Form,
  List,
  Row,
  SaveBar,
  Section,
  StepperField,
  StepperLabel,
  Wide,
} from './guide-editor.styled';
import { CODE_IN_TEXT_COPY, STALE_GUIDE_COPY, expiryHint, leadHint, moved, revealHint } from './guide-model';

/**
 * THE GUIDE TAB (plan S7.8): the guide's words and timings, in six sections
 * with h2s (FIXED copy). One PUT with only the changed fields and the version
 * the form was opened on:
 *   409 STALE_GUIDE  → a banner "Someone saved this guide a moment ago…" + Reload;
 *   422 CODE_IN_TEXT → the field's error "This text contains a door code…" and
 *                      focus moves there;
 *   400              → the server's sentence under each named field.
 * Without stays:manage every control is read-only and the save is not drawn
 * (the shell shows the reason once).
 */

type Draft = Pick<StaffGuide, GuideContentField>;

const FIELDS: ReadonlyArray<GuideContentField> = [
  'portal_lead_hours',
  'reveal_minutes',
  'expiry_minutes',
  'address_text',
  'maps_url',
  'directions',
  'parking_space',
  'parking_instructions',
  'host_name',
  'host_phone',
  'welcome_message',
  'air_conditioning',
  'heating',
  'hot_water',
  'rubbish',
  'quiet_hours',
  'checkout_instructions',
  'checkout_checklist',
  'emergency_phone',
  'content_language',
];

const toDraft = (g: StaffGuide): Draft => {
  const out = {} as Record<string, unknown>;
  for (const k of FIELDS) out[k] = k === 'checkout_checklist' ? [...(g.checkout_checklist || [])] : g[k];

  return out as Draft;
};

const cleanList = (list: string[]): string[] => list.map(s => s.trim()).filter(Boolean);

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The fields that differ from what the server holds (checklist blanks dropped). */
const changesOf = (draft: Draft, base: StaffGuide): Partial<Draft> => {
  const out: Partial<Draft> = {};
  for (const k of FIELDS) {
    const value = k === 'checkout_checklist' ? cleanList(draft.checkout_checklist) : draft[k];
    const was = base[k];
    if (!same(value, was)) (out as Record<string, unknown>)[k] = value;
  }

  return out;
};

export const fieldId = (field: string): string => `guide-field-${field}`;

const LANGUAGES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'es', label: 'Spanish' },
  { value: '', label: 'Other' },
];

export interface GuideEditorFormProps {
  listingId: string;
  bundle: GuideBundle;
  canManage: boolean;
  onSaved: (guide: StaffGuide) => void;
  onReload: () => void | Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
}

export const GuideEditorForm: React.FC<GuideEditorFormProps> = ({
  listingId,
  bundle,
  canManage,
  onSaved,
  onReload,
  onDirtyChange,
}) => {
  const base = bundle.guide;
  const [draft, setDraft] = useState<Draft>(() => toDraft(base));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const desk = useDeskInPlan();
  const [saved, setSaved] = useState(false);
  const [stale, setStale] = useState(false);
  const [focusField, setFocusField] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const inFlight = useRef(false);

  // A new server copy (a save, a reload) is the form's new starting point.
  useEffect(() => {
    setDraft(toDraft(base));
    setStale(false);
  }, [base]);

  const changes = useMemo(() => changesOf(draft, base), [draft, base]);
  const dirty = Object.keys(changes).length > 0;

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // Focus follows a refusal to the field it names.
  useEffect(() => {
    if (!focusField) return;
    const el = document.getElementById(fieldId(focusField));
    if (el) el.focus();
    setFocusField(null);
  }, [focusField]);

  const set = <K extends GuideContentField>(key: K, value: Draft[K]) => {
    setSaved(false);
    setDraft(d => ({ ...d, [key]: value }));
    if (errors[key]) setErrors(e => {
      const next = { ...e };
      delete next[key];

      return next;
    });
  };

  const save = async () => {
    if (!canManage || inFlight.current) return;
    if (!dirty) {
      setSaved(true);

      return;
    }
    inFlight.current = true;
    setBusy(true);
    setErrors({});
    setStale(false);
    try {
      const { guide } = await saveGuide(listingId, { ...changes, version: base.version });
      onSaved(guide);
      setSaved(true);
      toast.success('Guide saved');
    } catch (err) {
      const e = err as StaysApiError;
      if (e && e.code === 'STALE_GUIDE') {
        setStale(true);
      } else if (e && e.code === 'CODE_IN_TEXT') {
        const field = e.field || '';
        if (field) {
          setErrors({ [field]: CODE_IN_TEXT_COPY });
          setFocusField(field);
        } else toast.error(CODE_IN_TEXT_COPY);
      } else if (e && e.status === 400 && Object.keys(e.fields || {}).length) {
        setErrors(e.fields);
        const first = FIELDS.find(k => e.fields[k]);
        if (first) setFocusField(first);
        else toast.error(refusalText(e, 'Could not save the guide. Try again.'));
      } else {
        toast.error(refusalText(e, 'Could not save the guide. Try again.'));
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const reload = async () => {
    setReloading(true);
    try {
      await onReload();
    } finally {
      setReloading(false);
    }
  };

  const readOnly = !canManage;
  const err = (k: string) => errors[k];
  const describedBy = (k: string, hint?: boolean) =>
    [hint ? `${fieldId(k)}-hint` : '', errors[k] ? `${fieldId(k)}-error` : ''].filter(Boolean).join(' ') || undefined;

  const input = (
    k: GuideContentField,
    label: string,
    max: number,
    opts: { type?: string; hint?: string | null; inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'] } = {}
  ) => (
    <Field>
      <Label htmlFor={fieldId(k)}>{label}</Label>
      <Input
        id={fieldId(k)}
        type={opts.type || 'text'}
        inputMode={opts.inputMode}
        value={String(draft[k] ?? '')}
        maxLength={max}
        readOnly={readOnly}
        hasError={!!err(k)}
        aria-invalid={err(k) ? true : undefined}
        aria-describedby={describedBy(k, !!opts.hint)}
        onChange={e => set(k, e.target.value as Draft[typeof k])}
      />
      {opts.hint ? <FieldHint id={`${fieldId(k)}-hint`}>{opts.hint}</FieldHint> : null}
      <FieldError id={`${fieldId(k)}-error`} message={err(k)} />
    </Field>
  );

  const area = (k: GuideContentField, label: string, max: number, rows = 4) => (
    <Field>
      <CountedTextarea
        id={fieldId(k)}
        label={label}
        value={String(draft[k] ?? '')}
        max={max}
        rows={rows}
        readOnly={readOnly}
        hasError={!!err(k)}
        aria-describedby={errors[k] ? `${fieldId(k)}-error` : undefined}
        onChange={e => set(k, e.target.value as Draft[typeof k])}
      />
      <FieldError id={`${fieldId(k)}-error`} message={err(k)} />
    </Field>
  );

  const list = draft.checkout_checklist;
  const listMax = bundle.limits?.checklist || 10;
  const setList = (next: string[]) => set('checkout_checklist', next);
  const checkIn = bundle.listing?.check_in_time || '';
  const checkOut = bundle.listing?.check_out_time || '';

  return (
    <Form
      noValidate
      aria-label="Guide"
      onSubmit={e => {
        e.preventDefault();
        void save();
      }}
    >
      {stale ? (
        <Banner $tone="warning" role="alert" data-testid="guide-stale">
          <BannerText>{STALE_GUIDE_COPY}</BannerText>
          <Row>
            <AppButton variant="secondary" size="compact" loading={reloading} loadingLabel="Reloading" onClick={() => void reload()}>
              Reload
            </AppButton>
          </Row>
        </Banner>
      ) : null}

      <Section aria-labelledby="guide-h-timing">
        <H2 id="guide-h-timing">Guest access timing</H2>
        <StepperField>
          <StepperLabel>Guide opens (hours before check-in)</StepperLabel>
          <Stepper
            label="hours before check-in"
            groupLabel="Guide opens (hours before check-in)"
            value={draft.portal_lead_hours}
            min={1}
            max={168}
            disabled={readOnly}
            onChange={n => set('portal_lead_hours', n)}
          />
          <FieldHint>{leadHint(draft.portal_lead_hours)}</FieldHint>
          <FieldError message={err('portal_lead_hours')} />
        </StepperField>
        <StepperField>
          <StepperLabel>Door codes appear (minutes before check-in)</StepperLabel>
          <Stepper
            label="minutes before check-in"
            groupLabel="Door codes appear (minutes before check-in)"
            value={draft.reveal_minutes}
            min={0}
            max={180}
            step={5}
            disabled={readOnly}
            onChange={n => set('reveal_minutes', n)}
          />
          <FieldHint data-testid="guide-reveal-hint">{revealHint(checkIn, draft.reveal_minutes)}</FieldHint>
          <FieldError message={err('reveal_minutes')} />
        </StepperField>
        <StepperField>
          <StepperLabel>Access ends (minutes after check-out)</StepperLabel>
          <Stepper
            label="minutes after check-out"
            groupLabel="Access ends (minutes after check-out)"
            value={draft.expiry_minutes}
            min={0}
            max={1440}
            step={15}
            disabled={readOnly}
            onChange={n => set('expiry_minutes', n)}
          />
          <FieldHint>{expiryHint(checkOut, draft.expiry_minutes)}</FieldHint>
          <FieldError message={err('expiry_minutes')} />
        </StepperField>
      </Section>

      <Section aria-labelledby="guide-h-arrival">
        <H2 id="guide-h-arrival">Arrival</H2>
        <FieldGrid>
          {input('address_text', 'Address', 200, {
            hint: !draft.address_text.trim() && bundle.defaults?.address ? `${w('buildingAddress', desk)}: ${bundle.defaults.address}` : null,
          })}
          {input('maps_url', 'Maps link', 500, { type: 'url', inputMode: 'url' })}
          <Wide>{area('directions', 'Directions', 4000, 5)}</Wide>
          {input('parking_space', 'Parking space', 200)}
          <Wide>{area('parking_instructions', 'Parking instructions', 2000)}</Wide>
        </FieldGrid>
      </Section>

      <Section aria-labelledby="guide-h-host">
        <H2 id="guide-h-host">Host card</H2>
        <FieldGrid>
          {input('host_name', 'Host name', 200)}
          {input('host_phone', 'Host phone', 40, { type: 'tel', inputMode: 'tel' })}
          <Wide>{area('welcome_message', 'Welcome message', 600, 3)}</Wide>
        </FieldGrid>
      </Section>

      <Section aria-labelledby="guide-h-home">
        <H2 id="guide-h-home">Using the home</H2>
        {area('air_conditioning', 'Air conditioning', 2000)}
        {area('heating', 'Heating', 2000)}
        {area('hot_water', 'Hot water', 2000)}
        {area('rubbish', 'Rubbish & recycling', 2000)}
      </Section>

      <Section aria-labelledby="guide-h-rules">
        <H2 id="guide-h-rules">Rules and check-out</H2>
        {input('quiet_hours', 'Quiet hours', 200)}
        {area('checkout_instructions', 'Check-out instructions', 4000, 5)}
        <Field>
          <Label as="span" id="guide-checklist-label">Check-out checklist</Label>
          <div id={fieldId('checkout_checklist')} tabIndex={-1} role="group" aria-labelledby="guide-checklist-label"
            aria-describedby={errors.checkout_checklist ? `${fieldId('checkout_checklist')}-error` : undefined}>
            <List>
              {list.map((item, i) => (
                <ChecklistRow key={i}>
                  <Input
                    id={`${fieldId('checkout_checklist')}-${i}`}
                    aria-label={`Check-out checklist item ${i + 1}`}
                    value={item}
                    maxLength={120}
                    readOnly={readOnly}
                    hasError={!!errors.checkout_checklist}
                    onChange={e => setList(list.map((x, j) => (j === i ? e.target.value : x)))}
                  />
                  {canManage ? (
                    <>
                      <AppButton variant="ghost" size="compact" disabled={i === 0}
                        aria-describedby={`${fieldId('checkout_checklist')}-${i}`}
                        onClick={() => setList(moved(list, i, -1))}>
                        Move up
                      </AppButton>
                      <AppButton variant="ghost" size="compact" disabled={i === list.length - 1}
                        aria-describedby={`${fieldId('checkout_checklist')}-${i}`}
                        onClick={() => setList(moved(list, i, 1))}>
                        Move down
                      </AppButton>
                      <AppButton variant="ghost" size="compact" onClick={() => setList(list.filter((_, j) => j !== i))}>
                        {`Remove item ${i + 1}`}
                      </AppButton>
                    </>
                  ) : null}
                </ChecklistRow>
              ))}
            </List>
          </div>
          <FieldError id={`${fieldId('checkout_checklist')}-error`} message={err('checkout_checklist')} />
          {canManage ? (
            <Row>
              <ActionWithReason reason={list.length >= listMax ? `A checklist can have up to ${listMax} items.` : null}>
                {reasonId => (
                  <AppButton
                    variant="secondary"
                    size="compact"
                    disabled={list.length >= listMax}
                    aria-describedby={reasonId}
                    onClick={() => {
                      setList([...list, '']);
                      setFocusField(`checkout_checklist-${list.length}`);
                    }}
                  >
                    Add item
                  </AppButton>
                )}
              </ActionWithReason>
            </Row>
          ) : null}
        </Field>
        {input('emergency_phone', 'Emergency number', 40, {
          type: 'tel',
          inputMode: 'tel',
          hint: bundle.defaults?.emergency_phone ? `Leave blank to use ${bundle.defaults.emergency_phone}` : null,
        })}
      </Section>

      <Section aria-labelledby="guide-h-language">
        <H2 id="guide-h-language">Language</H2>
        <Field>
          <Label htmlFor={fieldId('content_language')}>Written in</Label>
          <Select
            id={fieldId('content_language')}
            value={draft.content_language || ''}
            disabled={readOnly}
            aria-describedby={describedBy('content_language')}
            onChange={e => set('content_language', e.target.value || null)}
          >
            {LANGUAGES.map(o => (
              <option key={o.value || 'other'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <FieldError id={`${fieldId('content_language')}-error`} message={err('content_language')} />
        </Field>
      </Section>

      {canManage ? (
        <SaveBar>
          <AppButton type="submit" variant="primary" loading={busy} loadingLabel="Saving…" success={saved && !dirty} successLabel="Saved">
            Save guide
          </AppButton>
        </SaveBar>
      ) : null}
    </Form>
  );
};
